import { LRUMap } from 'lru_map';
import { assertCanHandle, isNil } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats, withStatsSync } from '#common/stats';
import { FILESPACE_V8, FILESPACE_V12, InvalidArgumentError, InvalidPathError, NotImplementedError, } from '#common/types';
import { canHandleAllErrors } from '#common/util';
import { OptionalBoolean } from '#config/types';
import { CryptoTuple } from '#crypto';
import { ExternalFilesHelper, makeExternalFileLinkedEvent, makeExternalFileLinkFailedEvent, } from '#external-files';
import { BasePointerKind, ExternalFileKind, } from '#external-files/types';
import { NullTelemetry } from '#filespace/types';
import { Begin, } from '#wasm/modules';
import { FsIoStats, FsIoStatsFileType, isFsIoStatsSupported, toFsIoStatsFileType } from './fs-io-stats';
import { FsEntryType, FsLimits, } from './types/basic-types';
import { FileReadOnlyError } from './types/errors';
import { toFsEntryKey, } from './types/filesystem-extended';
import { FileSystemStats } from './types/stats';
let maxReaddirResults = 4_294_967_294;
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const INODE_CACHE_CAPACITY = 100;
const MIN_HARD_LINKS_FS_VERSION = FILESPACE_V12;
const HARD_LINK_CREATED_EVENT_NAME = 'HardLinkCreated';
export const setMaxReaddirResults = (value) => (maxReaddirResults = value);
export const createFileSystem = (params) => {
    const externalFilesHelper = new ExternalFilesHelper(params.tupleWasm, params.dataStores, new CryptoTuple(params.tupleWasm, params.adminCryptoProvider), params.fsModule, params.commonConfigKeyHelper);
    return new FileSystemImpl(params.fsModule, params.fileStore, params.fileNodeManager, params.userActivityTracker, params.configValidator, params.settingsCache, params.logStoreWasm, params.utilsWasm, params.keyValueHelpers, externalFilesHelper, params.fsVersion, params.metaStore, params.nodeId, params.tupleWasm, params.statsFlusher, params.fsStatsIntervalMs, params.logTelemetry);
};
class DirReader {
    fsModule;
    dirId;
    callStats;
    dirResult = { entries: [], cursor: Begin() };
    current = -1;
    constructor(fsModule, dirId, callStats) {
        this.fsModule = fsModule;
        this.dirId = dirId;
        this.callStats = callStats;
    }
    [Symbol.asyncIterator]() {
        return this;
    }
    async next() {
        return withStats(this.callStats, async () => {
            const available = this.dirResult.entries.length;
            ++this.current;
            if (this.current === available) {
                if (available && !this.dirResult.cursor) {
                    return { done: true, value: undefined };
                }
                this.dirResult = await this.fsModule.readDir(this.dirId, maxReaddirResults, this.dirResult.cursor);
                this.current = 0;
            }
            if (this.dirResult.entries.length === 0) {
                return { done: true, value: undefined };
            }
            const entry = this.dirResult.entries[this.current];
            return { done: false, value: { ...entry, key: toFsEntryKey(entry.key) } };
        });
    }
}
export class FileSystemImpl {
    fsModule;
    fileStore;
    fileNodeManager;
    userActivityTracker;
    configValidator;
    settingsCache;
    logStoreWasm;
    utilsWasm;
    keyValueHelpers;
    externalFilesHelper;
    fsVersion;
    fsStatsIntervalMs;
    logTelemetry;
    log = Logging.getLogger('FileSystem');
    fsStats = new FileSystemStats(this.log);
    fsLimits = {
        limits: new FsLimits(0n, 0n, 0n, 0n, 0n),
        lastTimeStatsUpdated: 0,
    };
    inodeCache;
    fsIoStats;
    constructor(fsModule, fileStore, fileNodeManager, userActivityTracker, configValidator, settingsCache, logStoreWasm, utilsWasm, keyValueHelpers, externalFilesHelper, fsVersion, metaStore, nodeId, tupleWasm, statsFlusher, fsStatsIntervalMs = MINUTE, logTelemetry = NullTelemetry) {
        this.fsModule = fsModule;
        this.fileStore = fileStore;
        this.fileNodeManager = fileNodeManager;
        this.userActivityTracker = userActivityTracker;
        this.configValidator = configValidator;
        this.settingsCache = settingsCache;
        this.logStoreWasm = logStoreWasm;
        this.utilsWasm = utilsWasm;
        this.keyValueHelpers = keyValueHelpers;
        this.externalFilesHelper = externalFilesHelper;
        this.fsVersion = fsVersion;
        this.fsStatsIntervalMs = fsStatsIntervalMs;
        this.logTelemetry = logTelemetry;
        if (fsVersion >= MIN_HARD_LINKS_FS_VERSION) {
            this.inodeCache = new LRUMap(INODE_CACHE_CAPACITY);
        }
        this.fsStats.fileStore = fileStore.stats();
        this.fsIoStats = new FsIoStats(nodeId, this.keyValueHelpers, metaStore, tupleWasm, this.log, isFsIoStatsSupported(fsVersion), statsFlusher);
    }
    stop() {
        this.log.trace(`Stopping FileSystem...`);
        this.log.trace(`FileSystem Stopped.`);
    }
    async getPath(entryKey) {
        return withStats(this.fsStats.getPath, async () => {
            const path = await this.fsModule.getPath(entryKey.id);
            this.fsStats.getPath.updatePathLen(path);
            return path;
        }, { entryKey });
    }
    async getPathsByIdsBulk(ids) {
        return withStats(this.fsStats.getPathsByIdsBulk, async () => {
            return this.fsModule.getPathsByIdsBulk(ids);
        }, { count: ids.length });
    }
    async getMetadata(entryKey) {
        return withStats(this.fsStats.getMetadata, async () => {
            if (await this.fsModule.isRootId(entryKey.id)) {
                return { size: 0n, creationTime: 0n, updateTime: 0n };
            }
            await this.checkReadPermissions(entryKey.id);
            const inodeId = await this.getInodeId(entryKey.id);
            const meta = await this.fileNodeManager.getMetadata(inodeId);
            return {
                size: meta.size,
                creationTime: meta.creationTime,
                updateTime: meta.updateTime,
            };
        }, { entryKey });
    }
    async getEntryKey(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.getEntryKey, async () => {
            const extendedEntryKey = await this.fsModule.getExtendedEntryKey(path);
            this.fsStats.getEntryKey.updatePathLen(path);
            this.cacheInodeId(extendedEntryKey);
            return toFsEntryKey(extendedEntryKey);
        }, { entryPath: path });
    }
    async resolve(entryPath) {
        entryPath = this.normalizePath(entryPath);
        return withStats(this.fsStats.resolve, async () => {
            const entry = await this.fsModule.resolve(entryPath);
            this.cacheInodeId(entry.key);
            return this.toFsEntry(entry);
        }, { entryPath });
    }
    async createFile(path) {
        await this.updateFsSettings();
        path = this.normalizePath(path);
        return withStats(this.fsStats.createFile, async () => {
            await this.trackUserActivity();
            const extendedKey = await this.fsModule.createFile(path);
            this.cacheInodeId(extendedKey);
            return toFsEntryKey(extendedKey);
        }, { filePath: path });
    }
    async readFile(entryKey, buffer, offset) {
        if (!(await this.isEntryOfType(entryKey, FsEntryType.FILE))) {
            throw new InvalidArgumentError('Writing is only allowed on files');
        }
        return withStats(this.fsStats.readFile, async () => {
            await this.trackUserActivity();
            this.validateOffset(offset);
            await this.checkReadPermissions(entryKey.id);
            const inodeId = await this.getInodeId(entryKey.id);
            if (await this.handleEmptyRead(inodeId, buffer.length, offset)) {
                return 0;
            }
            const cryptoKey = await this.fsModule.getAccess(entryKey.id);
            const bytesToRead = buffer.length;
            const read = await this.fileStore.get(inodeId, offset, bytesToRead, cryptoKey);
            buffer.set(read);
            this.fsStats.totalBytesRead += read.length;
            await this.recordFsIoStats(async () => {
                const fileType = toFsIoStatsFileType(await this.resolveBasePointerKind(inodeId));
                this.fsIoStats.incrementRead(fileType, read.length);
            });
            return read.length;
        }, { fileId: entryKey.id, offset, size: buffer.length });
    }
    async writeFile(entryKey, buffer, offset) {
        if (!(await this.isEntryOfType(entryKey, FsEntryType.FILE))) {
            throw new InvalidArgumentError('Writing is only allowed on files');
        }
        await this.updateFsLimits();
        return withStats(this.fsStats.writeFile, async () => {
            await this.doWriteFile(entryKey.id, buffer, offset);
            this.fsStats.totalBytesWritten += buffer.length;
        }, { fileId: entryKey.id, offset: offset, size: buffer.length });
    }
    async writeFileInternal(entryKey, buffer, offset) {
        if (!(await this.isEntryOfType(entryKey, FsEntryType.FILE))) {
            throw new InvalidArgumentError('Writing is only allowed on files');
        }
        await this.updateFsLimits();
        await this.doWriteFile(entryKey.id, buffer, offset, true);
    }
    async doWriteFile(linkId, buffer, offset, skipAuditCheck = false) {
        this.validateOffset(offset);
        await this.trackUserActivity();
        await this.checkReadWritePermissions(linkId, skipAuditCheck);
        const inodeId = await this.getInodeId(linkId);
        await this.validateExternalFileAccess(inodeId, 'write');
        await this.validateStorageAndDataLimit();
        const cryptoKey = await this.fsModule.getAccess(linkId);
        await this.fileStore.put(inodeId, offset, buffer, cryptoKey);
        await this.recordFsIoStats(() => this.fsIoStats.incrementWrite(FsIoStatsFileType.REGULAR_FILE, buffer.length));
    }
    deleteFile(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.deleteFile, async () => {
            await this.trackUserActivity();
            const extendedKey = await this.fsModule.getExtendedEntryKey(path);
            await this.fsModule.deleteFile(path);
            this.fileNodeManager.invalidateCache(extendedKey.inodeId);
        }, { filePath: path });
    }
    async createHardLink(targetPath, linkPath) {
        await this.updateFsSettings();
        targetPath = this.normalizePath(targetPath);
        linkPath = this.normalizePath(linkPath);
        return withStats(this.fsStats.createHardLink, async () => {
            await this.trackUserActivity();
            const extendedKey = await this.fsModule.createHardLink(targetPath, linkPath);
            this.cacheInodeId(extendedKey);
            this.logTelemetry({
                name: HARD_LINK_CREATED_EVENT_NAME,
                data: {
                    customData: {
                        inodeId: extendedKey.inodeId,
                        entryId: extendedKey.linkId,
                    },
                },
            });
            return toFsEntryKey(extendedKey);
        }, { linkPath, targetPath });
    }
    async createDir(path) {
        await this.updateFsSettings();
        path = this.normalizePath(path);
        return withStats(this.fsStats.createDir, async () => {
            await this.trackUserActivity();
            const extendedKey = await this.fsModule.createDir(path);
            return toFsEntryKey(extendedKey);
        }, { dirPath: path });
    }
    async createDirById(parentId, name) {
        await this.updateFsSettings();
        name = name.normalize('NFD');
        return withStats(this.fsStats.createDirById, async () => {
            await this.trackUserActivity();
            return this.toFsEntry(await this.fsModule.createDirById(parentId, name));
        }, { parentId, name });
    }
    readDir(entryKey) {
        return withStatsSync(this.fsStats.readDir, () => new DirReader(this.fsModule, entryKey.id, this.fsStats.readDirIterator), { dirKey: entryKey });
    }
    readDirPaginated(entryId, limit, cursor) {
        return withStats(this.fsStats.readDirPaginated, async () => {
            const result = await this.fsModule.readDir(entryId, limit, cursor);
            return {
                items: result.entries.map((entry) => this.toFsEntry(entry)),
                nextCursor: result.cursor,
            };
        }, { dirKey: entryId, limit });
    }
    deleteDir(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.deleteDir, async () => {
            await this.trackUserActivity();
            await this.fsModule.deleteDir(path);
        }, { dirPath: path });
    }
    deleteDirById(dirId) {
        return withStats(this.fsStats.deleteDirById, async () => {
            await this.trackUserActivity();
            await this.fsModule.deleteDirById(dirId);
        }, { dirId });
    }
    fileExists(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.fileExists, async () => {
            return await this.fsModule.fileExists(path);
        }, { filePath: path });
    }
    dirExists(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.dirExists, async () => {
            return await this.fsModule.dirExists(path);
        }, { dirPath: path });
    }
    async createLink(targetPath, linkPath) {
        targetPath = this.fsVersion >= FILESPACE_V8 ? targetPath.normalize('NFD') : targetPath;
        linkPath = this.normalizePath(linkPath);
        return withStats(this.fsStats.createLink, async () => {
            await this.trackUserActivity();
            const extendedKey = await this.fsModule.createLink(targetPath, linkPath);
            return toFsEntryKey(extendedKey);
        }, { linkPath, targetPath });
    }
    readLink(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.readLink, async () => {
            return await this.fsModule.readLink(path);
        }, { linkPath: path });
    }
    deleteLink(path) {
        path = this.normalizePath(path);
        return withStats(this.fsStats.deleteLink, async () => {
            await this.trackUserActivity();
            await this.fsModule.deleteLink(path);
        }, { linkPath: path });
    }
    move(sourcePath, destinationPath, overwrite) {
        sourcePath = this.normalizePath(sourcePath);
        destinationPath = this.normalizePath(destinationPath);
        return withStats(this.fsStats.move, async () => {
            await this.trackUserActivity();
            const dstEntryKey = overwrite ? await this.getEntryGuarded(destinationPath) : undefined;
            await this.fsModule.move(sourcePath, destinationPath, overwrite);
            if (overwrite && dstEntryKey) {
                this.fileNodeManager.invalidateCache(dstEntryKey.inodeId);
            }
        }, { sourcePath, destinationPath, overwrite });
    }
    setUpdateTime(_path, _updateTime) {
        // eslint-disable-next-line no-useless-assignment -- v8 new core rule; leaving the explicit initializer for the team to assess
        _path = this.normalizePath(_path);
        return Promise.reject(new NotImplementedError());
    }
    setEndOfFile(_path, _size) {
        // eslint-disable-next-line no-useless-assignment -- v8 new core rule; leaving the explicit initializer for the team to assess
        _path = this.normalizePath(_path);
        return Promise.reject(new NotImplementedError());
    }
    stats() {
        return this.fsStats;
    }
    getEntryInfoById(entryId) {
        return withStats(this.fsStats.getEntryInfoById, async () => {
            const entry = await this.fsModule.getEntryInfoById(entryId);
            this.cacheInodeId(entry.key);
            return this.toFsEntry(entry);
        }, { entryId });
    }
    async link(path, file) {
        let extendedKey;
        try {
            await this.configValidator.verifyExternalFilesEnabled();
            await this.updateFsSettings();
            path = this.normalizePath(path);
            extendedKey = await this.createExternalFile(path, file);
        }
        catch (err) {
            this.logTelemetry(makeExternalFileLinkFailedEvent(file, err));
            throw err;
        }
        this.cacheInodeId(extendedKey);
        this.logTelemetry(makeExternalFileLinkedEvent(file));
        return toFsEntryKey(extendedKey);
    }
    createExternalFile(path, file) {
        switch (file.kind) {
            case ExternalFileKind.SINGLE_OBJECT:
                return this.createSingleObjectFile(path, file);
            case ExternalFileKind.HTTP_LINK:
                return this.createHttpFile(path, file);
            default:
                return Promise.reject(new NotImplementedError());
        }
    }
    async updateHttpFileUrl(path, url) {
        await this.configValidator.verifyExternalFilesEnabled();
        path = this.normalizePath(path);
        const extendedKey = await this.externalFilesHelper.updateHttpFileUrl(path, url);
        this.fileNodeManager.invalidateCache(extendedKey.inodeId);
    }
    setPresignedUrlExpiresInSeconds(value) {
        this.externalFilesHelper.setExpiresInSeconds(value);
    }
    async getInodeId(linkId) {
        if (this.fsVersion < MIN_HARD_LINKS_FS_VERSION) {
            return linkId;
        }
        const cached = this.inodeCache?.get(linkId);
        if (!isNil(cached)) {
            return cached;
        }
        const inodeId = await this.fsModule.getInodeId(linkId);
        this.inodeCache?.set(linkId, inodeId);
        return inodeId;
    }
    cacheInodeId(key) {
        this.inodeCache?.set(key.linkId, key.inodeId);
    }
    toFsEntry(entry) {
        return { ...entry, key: toFsEntryKey(entry.key) };
    }
    async isEntryOfType(entryKey, expectedType) {
        return entryKey.type === expectedType && (await this.fsModule.getEntryTypeById(entryKey.id)) === expectedType;
    }
    async trackUserActivity() {
        await this.userActivityTracker.track();
    }
    async handleEmptyRead(inodeId, bufferLength, offset) {
        if (bufferLength === 0) {
            return true;
        }
        const fileSize = await this.fileNodeManager.getSize(inodeId);
        if (fileSize <= offset) {
            return true;
        }
        return false;
    }
    validateOffset(offset) {
        if (offset < 0) {
            throw new InvalidArgumentError('Negative offsets are not supported', offset.toString());
        }
    }
    async checkReadPermissions(linkId) {
        await this.fsModule.checkReadPermissions(linkId);
    }
    async checkReadWritePermissions(linkId, skipAuditCheck = false) {
        await this.fsModule.checkReadWritePermissions(linkId, skipAuditCheck);
    }
    async validateExternalFileAccess(inodeId, desiredAccess) {
        if (desiredAccess !== 'write') {
            return;
        }
        const isEnabled = await this.settingsCache.getExternalFilesEnabled();
        if (isEnabled === OptionalBoolean.UNSET) {
            return;
        }
        const layoutKvs = await this.fileNodeManager.getLayoutKeyRange(inodeId);
        const layout = await this.logStoreWasm.readLayout(inodeId, layoutKvs, 0, 1);
        if (layout.basePtr.kind !== BasePointerKind.NONE) {
            throw new FileReadOnlyError();
        }
    }
    // Stats must never fail a file operation.
    async recordFsIoStats(action) {
        if (!this.fsIoStats.isEnabled()) {
            return;
        }
        try {
            await action();
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            this.log.warn({ errorMessage: err }, 'Failed to record fs io stats.');
        }
    }
    async resolveBasePointerKind(inodeId) {
        const isEnabled = await this.settingsCache.getExternalFilesEnabled();
        if (isEnabled === OptionalBoolean.UNSET) {
            return BasePointerKind.NONE;
        }
        const layoutKvs = await this.fileNodeManager.getLayoutKeyRange(inodeId);
        return this.keyValueHelpers.parseBasePointer(layoutKvs).kind;
    }
    async validateStorageAndDataLimit() {
        await this.fsModule.validateStorageAndDataLimit();
    }
    async updateFsSettings() {
        await this.updateFsLimits();
        await this.updateForbidSpecialCharacters();
    }
    async updateFsLimits() {
        const fsLimits = await this.settingsCache.getFsLimits();
        if (!this.fsLimits.limits.isEqual(fsLimits)) {
            await this.resetFsLimits(fsLimits);
        }
        if (this.shouldUpdateFsStats()) {
            await this.updateFileSystemStats();
        }
    }
    async resetFsLimits(newFsLimits) {
        this.fsLimits.limits = newFsLimits;
        this.fsLimits.lastTimeStatsUpdated = 0;
        await this.fsModule.setFsLimits(this.fsLimits.limits);
    }
    shouldUpdateFsStats() {
        return (!this.fsLimits.limits.isUnlimited() &&
            Date.now() - this.fsLimits.lastTimeStatsUpdated > this.fsStatsIntervalMs);
    }
    async updateFileSystemStats() {
        await this.fsModule.updateFilesystemStats();
        this.fsLimits.lastTimeStatsUpdated = Date.now();
    }
    async updateForbidSpecialCharacters() {
        const isForbidden = await this.settingsCache.getForbidSpecialCharacter();
        await this.fsModule.updateForbidSpecialCharacters(isForbidden);
    }
    async getEntryGuarded(path) {
        try {
            return await this.fsModule.getExtendedEntryKey(path);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof InvalidPathError);
            return undefined;
        }
    }
    normalizePath(path) {
        return this.utilsWasm.normalizePath(path.normalize('NFD'));
    }
    async createSingleObjectFile(path, file) {
        return withStats(this.fsStats.createFile, () => this.externalFilesHelper.createSingleObjectFile(path, file), {
            filePath: path,
            objectId: file.objectId,
            dataStoreName: file.dataStoreName,
        });
    }
    async createHttpFile(normalizedFsEntryPath, file) {
        return withStats(this.fsStats.createFile, () => this.externalFilesHelper.createHttpFile(normalizedFsEntryPath, file), { filePath: normalizedFsEntryPath, url: file.url });
    }
}
